/**
 * 🐟 **Drawing the fish — T220, D115.**
 *
 * 🎯 **haugur, 2026-09-16:** *"i need fishes in the world… fish should be
 * placed randomly around and not be scarce."*
 *
 * ## 🔴 ONE object for every fish you can see, not one per fish and not one per
 * chunk
 *
 * **The rain is the precedent** — *"ONE object, made once, never rebuilt"* — and
 * the reasoning here is its sibling. A mesh per chunk would be a draw call per
 * chunk of sea and a rebuild every time one re-meshed; a mesh per fish would be
 * hundreds of draw calls. **This is one geometry, refilled on a timer.**
 *
 * ⚠️ **T218 is open and it is about this exact thread.** So the refill is
 * **ten times a second, not sixty**, it only looks at fish within
 * {@link FISH_VIEW}, and it writes into buffers it already owns rather than
 * allocating. **A fish that moves 5 cm between refills is a fish nobody can see
 * stutter.**
 *
 * ## ⚠️ Where the fish ARE is not decided here
 *
 * `shared/src/fish.ts` owns that, because **the spear will have to ask the
 * server the same question** (T221) and two copies of it would be two answers.
 * This module is the wrapper that puts them in the scene — the same split
 * `driftgeom.ts` and `driftmesh.ts` already use.
 */

import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  SRGBColorSpace,
  TextureLoader,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { attribute, texture, uniform, uv, vec3 } from 'three/tsl';
import { FISH, fishByKey, rngFrom, seedFrom } from '@lofi/shared';
import type { CreaturesMessage } from '@lofi/shared';
import { buildFishCard, swimOffset, type FishShape } from './fishgeom.ts';
import { BODY_RINGS, buildFishBody, profileFrom, shadeFor, type FishProfile } from './fishbody.ts';

/**
 * 🐟 **Which fish have a SOLID BODY rather than two crossed cards** — T232.
 *
 * 🎯 **haugur, 2026-09-17:** *"i want to create bleikja as a 3d model and put it
 * into the ocean to see the differrence… i want to judge the difference in
 * apperance and feel."*
 *
 * 🔴 **EXACTLY ONE FISH IS IN HERE, AND THAT IS THE EXPERIMENT.** A bleikja
 * swims **beside** three card fish, on the same spine, in the same water, at the
 * same shoal budget, wearing a photograph from the same art loop — **so the only
 * thing haugur is judging is thickness.** ⚠️ **A body alone in an empty sea
 * would look better than a card alone in an empty sea for reasons that are about
 * the sea.**
 *
 * ✅ **If the answer is yes, turning the other three solid is three more entries
 * in this set** — nothing else in this file cares which kind a school is.
 *
 * ⚠️ **If the answer is no, it is one entry removed** and the bleikja goes on
 * swimming as a card, which is why the choice lives in a set rather than in a
 * fork through the drawing code.
 */
export const SOLID_FISH: ReadonlySet<string> = new Set(['thorskur', 'ufsi', 'lax', 'bleikja']);

/**
 * 🐟 **Where a fish's picture stops being the fish, 0–1.**
 *
 * ⚠️ **ONE copy, and the outline reader uses it too.** The card is cut out by
 * this threshold and {@link profileFrom} measures the silhouette by it, so **a
 * body built from a different threshold than the one the game draws with would
 * be a body that does not match its own picture.** 🔴 *A second way in that
 * forgets what the first one does* — designed out rather than found later.
 */
const FISH_ALPHA_TEST = 0.35;

/**
 * 🐟 **Read a decoded picture's outline, through a canvas.**
 *
 * ⚠️ **The only place in the fish code that touches the DOM**, which is why it
 * is a free function here rather than arithmetic in `fishbody.ts`: that module
 * stays testable on a workstation with no browser and no GPU, and this one line
 * of platform is kept out of it.
 *
 * 🔴 **`null` rather than a throw.** A 2D context can be refused — too many
 * live canvases, a hostile setting — and a fish that does not draw is better
 * than a frame that dies. **The school stays empty and says so in its stats.**
 */
function readProfile(
  image: CanvasImageSource,
  size: { width: number; height: number },
): FishProfile | null {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) {
    console.error('[fish] no 2d context, so no body outline');
    return null;
  }
  ctx.drawImage(image, 0, 0);
  const { data } = ctx.getImageData(0, 0, size.width, size.height);
  return profileFrom(data, size.width, size.height, CUTS + 1, FISH_ALPHA_TEST * 255);
}

/**
 * 🐟 **Where each fish's photograph lives.**
 *
 * ⚠️ **One picture per species, never an atlas**, and the reason is geometry
 * rather than taste: **the card is built once from the picture's own width over
 * its height**, and the three cut-outs are 2.59, 3.09 and 2.82 to one. Three
 * proportions are three cards, so they cannot share a buffer, and once they
 * cannot share a buffer there is nothing left for an atlas to save.
 *
 * 🎯 **The pictures haugur chose:** þorskur candidate ① on 2026-09-16, and
 * **ufsi ④ and lax ④ on 2026-09-17** — *"4 and 4"*.
 */
function pictureFor(kind: string): string {
  return `/fish/${kind}.png`;
}

/**
 * 🐟 **How far away a fish is still drawn, in metres.**
 *
 * 🔴 **THIS WAS 60 AND IT MADE THE SEA LOOK EMPTY.** 🎯 **haugur, standing on the
 * lava at 347654, 396869 and looking west over a whole bay: *"not a single fish
 * to be seen."*** ⚠️ **He was right, and the game was behaving correctly** — the
 * nearest water in the direction he was FACING begins 125 m out, so every fish
 * in view was beyond the cut-off. **There was water 25 m behind his left
 * shoulder, and it had fish in it.**
 *
 * 👉 **The lesson is about what a budget is measured against.** 60 m was chosen
 * against *how small is a fish before it is pointless* — and that is a real
 * question, but it is the wrong one. **The right one is: how far away is the
 * water somebody is looking at?** In a bay, hundreds of metres.
 *
 * 📏 **140 m**, where a 80 cm fish is about three pixels — still a mark on the
 * water rather than nothing. ⚠️ **It costs about 850 fish in open sea against
 * 150**, which is why the refill stopped allocating a card per fish in the same
 * change.
 */
export const FISH_VIEW = 140;

/**
 * ☀️ **How bright the day is, 0–1**, driven from the same sun the storehouses
 * read — so a fish dims with the world instead of glowing through the night.
 */
export const fishDaylight = uniform(1);

/**
 * 🌊 **How often the SERVER speaks, in seconds** — ten times a second, the same
 * beat `players` uses.
 *
 * ⚠️ **It is not how often the fish are drawn.** 🔴 **The geometry is rebuilt
 * every frame** — see `update` — because the whole job of the smoothing is to
 * put sixty positions a second between the server's ten.
 */
export const SERVER_BEAT = 0.1;

/**
 * 🐟 **One fish as the server last described it, plus what the body needs.**
 *
 * 🔴 **THE SPLIT THAT MATTERS: the server owns where it IS; the id owns what it
 * LOOKS LIKE.** Length and tail phase are hashed from the fish's own id, so they
 * are stable for its life, identical for every player, and **cost nothing on the
 * wire** — there is no reason to send a number that cannot change.
 *
 * ⚠️ **`x/y/z` is where it is DRAWN and `tx/ty/tz` is where the server says it
 * is.** The server speaks ten times a second and the screen draws sixty, so
 * without the two the fish would step rather than swim.
 */
interface LiveFish {
  readonly id: string;
  /** Where it is drawn — chases the target. */
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Where the server last said it was. */
  tx: number;
  ty: number;
  tz: number;
  tyaw: number;
  /**
   * 🌊 **How fast it is actually going, measured between messages.**
   *
   * ⚠️ **Measured, not sent.** 🔴 **And it is what makes a fleeing fish LOOK
   * like it is fleeing** — the tail beats harder when it accelerates, because
   * `swimOffset` takes the rate as a multiple. A hashed constant could never do
   * that, and the first fish had exactly that fault: 🎯 *"the all have the same
   * move."*
   */
  speed: number;
  /**
   * 🐟 **How steeply it is nosing up or down, radians — MEASURED, not sent.**
   *
   * 🎯 **haugur, issue #662:** *"sometimes insted of swimming they swim flat."*
   *
   * ⚠️ **Worked out from how the fish actually moved between messages**, the
   * same way its tail beat is. 🔴 **So it can never disagree with what you see**
   * — a fish that is drawn climbing is a fish that climbed — and it costs
   * nothing on the wire.
   */
  pitch: number;
  /** What the pitch is heading toward, before smoothing. */
  tpitch: number;
  /** Its own size, hashed from its id — never changes. */
  readonly length: number;
  /** Where it is in its own tail-beat, hashed from its id. */
  readonly phase: number;
}

/**
 * 🌊 **How quickly a drawn fish catches up with where the server put it.**
 *
 * ⚠️ **A smoothing rate, not a speed.** Too low and a fish lags visibly behind
 * its own shadow; too high and the ten-per-second beat shows as a step.
 */
const CHASE = 12;

/**
 * 🐟 **The steepest a fish will nose up or down, radians.**
 *
 * ⚠️ **40°.** A photograph of a fish has no pectoral fins it can spread, so a
 * steeper climb reads as a fish standing on its tail rather than as one
 * swimming upward. 🔴 **A dial, and one for haugur's eye rather than for
 * arithmetic.**
 */
const MAX_PITCH = 0.7;

/**
 * How many vertices one fish costs. `buildFishCard` with 8 cuts is 9 rings of
 * two — kept as a constant so the buffers can be sized without building one.
 */
/**
 * 🔴 **HOW MANY DIVISIONS ALONG A FISH — AND EIGHT COST haugur A TAIL.**
 *
 * 🎯 **Issue #682, the first time he looked at a solid one:** *"ummm where is
 * the rest of its tail"*.
 *
 * 📏 **Measured on the bleikja's own photograph.** The tail lives in the last
 * **18%** of the body, and everything happens there:
 *
 * | along the fish | the outline's true depth |
 * |---|---|
 * | 0.82 — the tail's **waist** | **86 px** |
 * | 0.95 — the fan at its **widest** | **164 px** |
 * | 0.98 | **184 px** |
 *
 * ⚠️ **At eight cuts the stations near the tail are 0.75, 0.88 and 1.00** — so
 * it sampled neither the waist nor the flare, and **jumped straight over the
 * whole tail.** 🔴 **Meanwhile the middle of the fish, from 0.35 to 0.65, sits
 * between 250 and 262 px and barely changes: eight cuts spent its samples where
 * nothing happens and starved the one place the outline moves fastest.**
 *
 * ✅ **Twenty.** Stations every 5%, which puts four of them in the tail.
 * 📏 **210 vertices a fish against the card's 36**, so the whole 60-fish budget
 * is about **12,600 vertices a frame** — against a chunk mesher on the same
 * thread building 239,492 faces (T218, T230). ⚠️ **A dial, and the first thing
 * to turn if the fish ever cost a frame.**
 */
const CUTS = 20;
/* 🔴 TWO crossed cards, not one — see `buildFishCard`. A single upright card is
   edge-on from the shore, which is the only place anybody looks at a fish. */
const PLANES = 2;
const VERTS_PER_FISH = (CUTS + 1) * 2 * PLANES;
const INDICES_PER_FISH = CUTS * 6 * PLANES;

/**
 * 🐟 **And the same two counts for a SOLID body** — T232.
 *
 * 📏 **90 vertices against the card's 36, and 480 indices against 96.** ⚠️ **At
 * the bleikja's 10 of a 60-fish budget that is about 900 vertices a frame**,
 * against a chunk mesher on the same thread building 239,492 faces (T218, T230).
 * 🔴 **Written down rather than waved at**, because *"unmeasured" is not
 * "probably fine"*.
 */
const BODY_VERTS_PER_FISH = (CUTS + 1) * BODY_RINGS;
const BODY_INDICES_PER_FISH = CUTS * BODY_RINGS * 6;

/**
 * 🐟 **Turn a point's NORMAL to face the way the fish is going.**
 *
 * 🔴 **The same frame {@link cardOffset} uses, and it must stay that way** — a
 * body lit in one frame and drawn in another is the kind of fault that reads as
 * *"the light is wrong"* and is really *"the fish is wrong"*.
 *
 * ⚠️ **A normal is a DIRECTION, so it takes no position.** And the local axis
 * runs nose-at-0 toward the tail, while `cardOffset`'s `forward` points the way
 * the fish SWIMS — **so the body's own +x is MINUS forward**, which is the same
 * sign flip that once had every fish swimming backwards.
 *
 * ⚠️ **Exported so a test can assert it**, rather than a shader being believed.
 */
export function rotateNormal(
  nx: number,
  ny: number,
  nz: number,
  yaw: number,
  pitch: number,
): { x: number; y: number; z: number } {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const forward = { x: cy * cp, y: sp, z: sy * cp };
  const up = { x: -cy * sp, y: cp, z: -sy * sp };
  const sideways = { x: -sy, y: 0, z: cy };
  return {
    x: -nx * forward.x + ny * up.x + nz * sideways.x,
    y: -nx * forward.y + ny * up.y + nz * sideways.y,
    z: -nx * forward.z + ny * up.z + nz * sideways.z,
  };
}

/**
 * 🐟 **Where a point on the unit card lands, relative to the fish's own
 * position, for a fish facing `yaw`.**
 *
 * 🔴 **PULLED OUT OF THE DRAW LOOP SO A TEST CAN REACH IT**, on 2026-09-17,
 * because 🎯 haugur reported *"some of them still swim backwards"* and **a
 * direction is arithmetic, not a matter of opinion.**
 *
 * @param t where along the fish this point is — **0 is the NOSE, 1 is the
 *   TAIL**, which is the card's own convention (`fishgeom.ts` builds it
 *   nose-at-origin)
 * @param lz how far sideways, including the tail's wave
 */
export function cardOffset(
  t: number,
  ly: number,
  lz: number,
  length: number,
  yaw: number,
  pitch: number,
): { x: number; y: number; z: number } {
  /**
   * 🔴 **TWO AXES, NAMED, AND THAT IS THE WHOLE FIX.**
   *
   * ⚠️ **The old line was `lx = (t - 0.5) * length` rotated by `-yaw`, and it
   * was wrong twice over.** It put **t = 0 — the NOSE — at MINUS half a length**,
   * so every fish swam backwards; and it rotated the tail's sideways wave
   * through the same angle, which left the wave with a component **along** the
   * body, so a swimming fish stretched slightly instead of only bending.
   * 📏 **Measured: the nose sat −0.8 m from the tail on an 80 cm cod, and the
   * wave leaked `0.2 × sin(2 × yaw)` into the fish's own length.**
   *
   * ✅ **So: forward is where it is going, sideways is square to that.**
   */
  /**
   * 🔴 **THREE AXES NOW, AND THE THIRD IS WHY A FISH USED TO SWIM FLAT.**
   *
   * 🎯 **haugur, 2026-09-17, issue #662:** *"sometimes insted of swimming they
   * swim flat."*
   *
   * ⚠️ **A fish was only ever turned about the VERTICAL axis.** Its up-and-down
   * tilt was hard zero, so **one rising toward the surface stayed perfectly
   * level and slid upward like a lift.** 🔴 **Nobody could see it before,
   * because the old fish never changed depth at all** — the same shape of fault
   * as the backwards fish, found the same afternoon: **a new feature made an
   * old silence visible.**
   *
   * ✅ **`forward` tilts with the pitch, `up` tilts with it the other way, and
   * `sideways` is level** — a fish banks its nose, it does not roll.
   */
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);

  const forward = { x: cy * cp, y: sp, z: sy * cp };
  const up = { x: -cy * sp, y: cp, z: -sy * sp };
  /* ⚠️ Level on purpose: a fish noses up and down, it does not roll over. */
  const sideways = { x: -sy, y: 0, z: cy };

  /* 🔴 NOSE AHEAD. `fishgeom` builds the card nose-at-origin, so t = 0 is the
     nose and belongs at PLUS half a length along the heading. */
  const along = (0.5 - t) * length;
  return {
    x: along * forward.x + ly * up.x + lz * sideways.x,
    y: along * forward.y + ly * up.y + lz * sideways.y,
    z: along * forward.z + ly * up.z + lz * sideways.z,
  };
}

/**
 * 🐟 **Every fish you can currently see, as one object.**
 *
 * ⚠️ **Built empty and added to the scene immediately**, even inland where there
 * is no water at all. 🔴 **That is the smoke's lesson made a habit:** a material
 * whose shader is compiled the first time somebody walks onto a beach is a
 * shader nobody has ever run, and it would fail in front of haugur rather than
 * here. An empty draw costs nothing.
 */
export class FishSchool {
  readonly mesh: Mesh;
  private readonly geometry = new BufferGeometry();
  private readonly material = new MeshBasicNodeMaterial();
  private positions = new Float32Array(0);
  private uvs = new Float32Array(0);
  private indices = new Uint32Array(0);
  private capacity = 0;
  /** 🐟 Every fish the server has told us about, by id. */
  private readonly live = new Map<string, LiveFish>();
  /** When the server last spoke, for measuring how fast a fish moved. */
  private spokeAt = 0;
  /**
   * The picture's shape, so the card never stretches the fish.
   *
   * ⚠️ **Read off the photograph when it decodes.** The value here is only a
   * placeholder — nothing is drawn while {@link ready} is false, so a wrong
   * guess cannot reach the screen.
   */
  private aspect = 2.59;
  private ready = false;

  /**
   * 🐟 **Which species this school draws** — `thorskur`, `ufsi`, `lax`.
   *
   * 🔴 **It is also the filter.** A school ignores every creature that is not
   * its own kind, which is what keeps a fault in one fish out of the others.
   *
   * ⚠️ **Declared and assigned rather than a constructor parameter property**:
   * this package builds with `erasableSyntaxOnly`, which forbids those.
   */
  private readonly kind: string;

  /**
   * 🐟 **True for a fish with a solid body** — see {@link SOLID_FISH}.
   *
   * 🔴 **It changes the SHAPE and the LIGHT, and nothing else.** Same server
   * messages, same smoothing, same wave, same draw loop.
   */
  private readonly solid: boolean;

  /**
   * 🐟 **The fish's own outline, read off its photograph when it decodes.**
   *
   * ⚠️ **`null` until then, and a solid school draws nothing while it is** —
   * which is the same guard `ready` already provides for the card.
   */
  private profile: FishProfile | null = null;

  /** How many vertices and indices one of THIS school's fish costs. */
  private readonly vertsPerFish: number;
  private readonly indicesPerFish: number;

  /** 🔆 How lit each vertex is, for a solid body. Empty for a card school. */
  private shades = new Float32Array(0);

  constructor(kind: string) {
    /* 🔴 FIRST, before `load()` at the foot of this constructor asks for the
       picture by name — and before `stats` is labelled. */
    this.kind = kind;
    this.solid = SOLID_FISH.has(kind);
    this.vertsPerFish = this.solid ? BODY_VERTS_PER_FISH : VERTS_PER_FISH;
    this.indicesPerFish = this.solid ? BODY_INDICES_PER_FISH : INDICES_PER_FISH;
    this.stats.kind = kind;
    this.stats.solid = this.solid;
    this.material.transparent = true;
    /* ⚠️ A cut-out, exactly like the carved boards: the fish's outline is the
       picture's alpha. alphaTest rather than blending, so nothing has to be
       sorted against the water it swims in. */
    this.material.alphaTest = FISH_ALPHA_TEST;
    this.material.side = DoubleSide;
    this.mesh = new Mesh(this.geometry, this.material);
    /**
     * 🔴 **BEFORE THE WATER, AND THIS IS THE BUG THAT SHIPPED.**
     *
     * 🎯 **haugur, on the first build to reach staging: *"i cant find any."***
     * The fish were there, in the right places, swimming — **and every one of
     * them was being thrown away by the depth test.**
     *
     * ⚠️ **It is T158's own design doing exactly what it was built to do.** The
     * water writes depth on purpose: *"the nearest water surface wins and
     * everything behind it is rejected"*, which is what deletes the
     * water-seen-through-water problem. 🔴 **A fish is behind the surface you
     * are looking through — so the water wrote depth first and the fish failed.**
     *
     * ✅ **So the fish draw FIRST and the water blends over them**, which is
     * also what is physically happening: the sea is in front of the fish.
     *
     * 🔴 **The number must stay BELOW `WATER_RENDER_ORDER`.** They are not two
     * independent settings — they are one ordering, and the comment that used to
     * sit here said the opposite in good faith: *"a fish drawn before the sea
     * would be blended away by it."* **It is blended by it; it is not blended
     * away.**
     *
     * ⚠️ **Nothing in a test can see this.** The geometry, the positions and the
     * count were all correct on both sides of the fix.
     */
    this.mesh.renderOrder = 0;
    this.mesh.frustumCulled = false;
    this.grow(64);
    void this.load();
  }

  /**
   * Decode the picture. Nothing waits on it — a fish that will not decode
   * leaves the school invisible rather than the frame dead.
   */
  private async load(): Promise<void> {
    try {
      const map = await new TextureLoader().loadAsync(pictureFor(this.kind));
      map.colorSpace = SRGBColorSpace;
      const tile = texture(map, uv());
      /**
       * 🔴 **UNSHADED — and haugur found out why it had to be.**
       *
       * 🎯 **#647:** *"Light and shadow affect the fish in a weird way."* His
       * picture shows it exactly: **the fish on the left of the view are pale
       * and the ones on the right are almost black**, in the same daylight, at
       * the same depth, in the same water.
       *
       * ⚠️ **It was a LAMBERT material, and every vertex carried an UP normal.**
       * That is `Minecraft/13`'s *shade: false* trick — one normal for every
       * corner of both crossed planes, so the two cannot take different
       * directional shading and read as folded card.
       *
       * 🔴 **But a normal is not a switch.** Lambert still lights BY it, and a
       * double-sided face has its normal flipped on the back — so a fish seen
       * from its other flank was lit by a normal pointing at the sea floor.
       * **Half the shoal was black, and which half depended on where you stood.**
       *
       * ✅ **So: no directional light at all**, which is what *shade: false*
       * actually means. **The same unshaded material the flame uses**, for the
       * same reason — and this project's own rule is to prefer a material that
       * is already in use.
       *
       * ⚠️ **Still dimmed by the daylight**, off the same sun the storehouses
       * read, so a fish is not a lit cut-out at midnight.
       */
      const lit = vec3(tile.r, tile.g, tile.b).mul(fishDaylight.mul(0.75).add(0.25));
      /**
       * 🔆 **A SOLID BODY IS SHADED, AND A CARD STILL IS NOT** — T232.
       *
       * 🔴 **This is the second half of the experiment.** A round fish that is
       * lit flat looks exactly like a flat fish from the side, **so the only
       * thing thickness would buy is the outline from above** — and haugur
       * asked about *appearance and feel*, not about one camera angle.
       *
       * ⚠️ **AND IT DOES NOT REPEAT #647.** That fault was a Lambert material
       * lighting a CROSSED CARD by a faked normal, flipped on the back face —
       * *"half the shoal black, and which half depended on where you stood."*
       * ✅ **Here the light is arithmetic this project owns**: one float a
       * vertex, worked out on the CPU from the body's own outward normal in
       * {@link shadeFor}, **floored at 0.55 so no part of a fish can ever be
       * darker than that**, and tested without a GPU.
       *
       * 🔴 **No scene light is involved at all**, so nothing a renderer does can
       * switch it off or flip it.
       */
      const colour = this.solid ? lit.mul(attribute('shade', 'float')) : lit;
      this.material.colorNode = colour;
      (this.material as unknown as { opacityNode: unknown }).opacityNode = tile.a;
      this.material.needsUpdate = true;
      /* ⚠️ three types `image` as `any`; the picture's own proportions are what
         stop the card stretching the fish, so they are read rather than assumed. */
      const image = map.image as { width: number; height: number };
      if (image.width > 0 && image.height > 0) this.aspect = image.width / image.height;
      if (this.solid) this.profile = readProfile(map.image as CanvasImageSource, image);
      this.ready = true;
      console.info(`[fish] ${this.kind} is ready`);
    } catch (err: unknown) {
      console.error(`[fish] the ${this.kind} picture did not decode`, err);
    }
  }

  /**
   * The one card every fish is drawn from, at length 1, built once.
   *
   * ⚠️ **Rebuilt only if the picture's proportions change**, which happens once,
   * when the photograph decodes.
   */
  private card: FishShape | null = null;
  private cardAspect = 0;

  /**
   * 🐟 **The one shape every fish in this school is drawn from, at length 1.**
   *
   * 🔴 **A card school gets crossed cards; a solid school gets a body** — and
   * **the draw loop below cannot tell which it was handed**, which is the point.
   * Both are a {@link FishShape}: positions, normals, `along`, uvs, indices.
   *
   * ⚠️ **`null` while a solid school is still waiting for its outline.** A body
   * cannot be built without the photograph's silhouette, and drawing a guess
   * would put a fish in the sea that is not the fish in the picture.
   */
  private unitShape(): FishShape | null {
    const profile = this.profile;
    if (this.solid && profile === null) return null;
    if (this.card === null || this.cardAspect !== this.aspect) {
      this.card =
        this.solid && profile !== null
          ? buildFishBody(1, this.aspect, profile, CUTS)
          : buildFishCard(1, this.aspect, CUTS);
      this.cardAspect = this.aspect;
    }
    return this.card;
  }

  /**
   * 🔬 **What the school actually drew, for the instrument** — T220.
   *
   * 🔴 **This exists because two fixes were shipped on a guess.** 🎯 haugur:
   * *"i cant find any"*, then *"i sometime see siluettes of fish but they
   * disapear when i get close."* **Neither symptom could be told apart from the
   * outside**, and the pictures this workstation can take are of a fallback
   * renderer at 2 fps.
   *
   * ⚠️ **A number the page can be asked for is worth more than another
   * screenshot**: it separates *no fish were built* from *fish were built and
   * something ate them*.
   */
  readonly stats = {
    /**
     * 🔴 Which fish this line is about. A total cannot tell you the lax is
     * missing. ⚠️ **Filled by the constructor** — a field initialiser runs
     * before the constructor body, so it cannot read `this.kind` here.
     */
    kind: '',
    /* 🐟 Which kind of body this school draws — T232. 🔴 **A probe that
       cannot tell a body from a card cannot report on the experiment.** */
    solid: false,
    columns: 0,
    near: 0,
    drawn: 0,
    ready: false,
    /* 🔬 Where the player is and where the first fish is, in WORLD metres, so a
       probe can see WHICH of the two is in the wrong space. */
    eye: '',
    fish: '',
    gap: 0,
  };

  /** Make room for `n` fish, keeping the buffers we already own where possible. */
  private grow(n: number): void {
    if (n <= this.capacity) return;
    const want = Math.max(n, this.capacity * 2, 64);
    this.positions = new Float32Array(want * this.vertsPerFish * 3);
    this.uvs = new Float32Array(want * this.vertsPerFish * 2);
    this.indices = new Uint32Array(want * this.indicesPerFish);
    this.capacity = want;
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('uv', new BufferAttribute(this.uvs, 2));
    this.geometry.setIndex(new BufferAttribute(this.indices, 1));
    if (this.solid) {
      /* 🔆 One float a vertex: how lit this scrap of skin is. ⚠️ **Only a solid
         school has it**, and only a solid school's material reads it. */
      this.shades = new Float32Array(want * this.vertsPerFish);
      this.geometry.setAttribute('shade', new BufferAttribute(this.shades, 1));
    }
  }

  /**
   * 🐟 **Take the server's word for where every fish is** — protocol 30, T228.
   *
   * 🔴 **This replaced a FORMULA, and that is the whole of T228.** The fish used
   * to be `fishAt(x, z, surface, bed, seconds)` — a hash of the cell and the
   * clock — and 🎯 haugur described what that looked like exactly: *"its a xerox
   * copy of a picture of a cod floting in a sea that has no movement."*
   *
   * ⚠️ **A fish the server does not mention is GONE**, immediately. There is no
   * grace period, on purpose: a body nobody sends an update for stands there
   * forever, which is a trap `players` already records.
   */
  setFrom(message: CreaturesMessage, now: number): void {
    const seen = new Set<string>();
    for (const c of message.creatures) {
      /**
       * 🔴 **THIS LINE SAID `!== 'thorskur'` UNTIL 2026-09-17, AND IT WOULD
       * HAVE MADE BOTH NEW FISH INVISIBLE.**
       *
       * ⚠️ **The server could have kept, ticked and broadcast a perfect ufsi
       * and the client would have dropped it here without a word** — which from
       * the outside looks exactly like the server never spawning one. **A
       * silent drop is the most expensive kind of bug this project has**, and
       * this one was already written and waiting.
       *
       * ✅ **It is a per-species filter now**, so a school draws its own fish
       * and nobody else's.
       */
      if (c.kind !== this.kind) continue;
      seen.add(c.id);
      const had = this.live.get(c.id);
      if (had === undefined) {
        /* 🎲 Its body, drawn once from its own id — stable for its life, the
           same for every player, and nothing on the wire. */
        const rng = rngFrom(seedFrom(c.id));
        const species = fishByKey(c.kind);
        this.live.set(c.id, {
          id: c.id,
          x: c.x,
          y: c.y,
          z: c.z,
          yaw: c.yaw,
          tx: c.x,
          ty: c.y,
          tz: c.z,
          tyaw: c.yaw,
          speed: 0,
          pitch: 0,
          tpitch: 0,
          /* ±15%, so a shoal is not a row of identical fish. */
          length: (species?.length ?? 0.8) * (0.85 + rng() * 0.3),
          phase: rng(),
        });
        continue;
      }
      /* 🌊 Measured between messages — see `LiveFish.speed`. */
      const gap = Math.max(now - this.spokeAt, 1e-3);
      const moved = Math.hypot(c.x - had.tx, c.y - had.ty, c.z - had.tz);
      had.speed = moved / gap;
      /**
       * 🐟 **Nose up if it climbed, down if it dived.**
       *
       * ⚠️ **Against the FLAT distance, not the total** — `atan2` of the rise
       * over the run is the angle the body actually travelled along.
       * 🔴 **Clamped to 40°**: a photograph of a fish has no pectoral fins to
       * sell a steeper climb, and past that it reads as a fish standing on its
       * tail.
       */
      const run = Math.hypot(c.x - had.tx, c.z - had.tz);
      const rise = c.y - had.ty;
      const want = run < 1e-4 && Math.abs(rise) < 1e-4 ? 0 : Math.atan2(rise, run);
      had.tpitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, want));
      had.tx = c.x;
      had.ty = c.y;
      had.tz = c.z;
      had.tyaw = c.yaw;
    }
    for (const id of [...this.live.keys()]) if (!seen.has(id)) this.live.delete(id);
    this.spokeAt = now;
    this.stats.columns = this.live.size;
  }

  /**
   * 🐟 **Draw them.**
   *
   * @param seconds the world clock, for the tail
   * @param eye where the player is, in WORLD metres
   * @param origin the render origin, so the mesh sits in the same space as the
   *   terrain — ⚠️ **the thing that put bodies 347 km outside the world once**
   * @param dt seconds since the last frame
   */
  update(
    seconds: number,
    eye: { x: number; y: number; z: number },
    origin: { x: number; y: number; z: number },
    dt: number,
  ): void {
    this.stats.ready = this.ready;
    if (!this.ready) return;

    /* 🌊 Every fish chases where the server put it, every FRAME — this is the
       part that must not be throttled, or the smoothing is what stutters. */
    const chase = Math.min(1, dt * CHASE);
    const near: LiveFish[] = [];
    for (const f of this.live.values()) {
      f.x += (f.tx - f.x) * chase;
      f.y += (f.ty - f.y) * chase;
      f.z += (f.tz - f.z) * chase;
      /* ⚠️ The shortest way round, or a fish turning past due south spins the
         long way — which reads as a fish having a fit. */
      const delta = ((f.tyaw - f.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      f.yaw += delta * chase;
      /* ⚠️ No wrapping needed: pitch is bounded well inside half a turn, so the
         shortest way round is simply the difference. */
      f.pitch += (f.tpitch - f.pitch) * chase;
      const dx = f.x - eye.x;
      const dz = f.z - eye.z;
      if (dx * dx + dz * dz > FISH_VIEW * FISH_VIEW) continue;
      near.push(f);
    }

    /**
     * 🔴 **THE GEOMETRY IS REBUILT EVERY FRAME, AND THAT IS THE WHOLE POINT.**
     *
     * 🎯 **haugur, 2026-09-17:** *"when i fly over and land at the shore and see
     * the fish for the first time, their animations stutters alot, even to the
     * meter says 60fps."*
     *
     * ⚠️ **There was a gate here — rebuild only every `REFILL` = 0.1 s.** So
     * every fish's position was smoothed **sixty times a second and uploaded
     * ten**: the fish moved in **ten discrete steps a second** while the frame
     * counter honestly read 60. 🔴 **The smoothing above was being computed and
     * then thrown away.**
     *
     * 👉 **The gate was right for the fish it was written for and wrong for
     * these.** The OLD fish were derived: a refill meant re-running `fishAt`
     * over every water column in view — **hundreds of them**, which genuinely
     * had to be rationed. **A live fish is already in a list**; rebuilding is a
     * loop over what is there.
     *
     * 📏 **Measured cost at the current budget: 60 fish × 36 vertices × 3 floats
     * = 6,480 floats a frame, 389,000 a second.** ⚠️ **Against the chunk mesher
     * on the same thread that is nothing** — and T230 is the ticket that says so
     * with a number.
     *
     * 🔴 **THE GENERAL SHAPE, AND IT IS TODAY'S LESSON WEARING A THIRD COAT:**
     * *a limit inherited from the thing you replaced is a limit nobody chose.*
     */

    this.grow(near.length);
    const { positions, uvs, indices } = this;
    positions.fill(0);

    /**
     * 🔴 **ONE card, built once, at length 1 — never one per fish.**
     *
     * The first version called `buildFishCard` for every fish on every refill:
     * **850 cards a refill, ten times a second, each allocating five arrays.**
     * ⚠️ **That is exactly the shape of waste T218 is open about**, on the same
     * thread.
     *
     * ✅ **A fish is the unit card scaled.** Every position scales with length,
     * and so does the wave — `swimOffset`'s amplitude is a straight multiple of
     * it — so one card serves every fish at every size.
     */
    const card = this.unitShape();
    if (card === null) return;

    let v = 0;
    let i = 0;
    for (const f of near) {
      const base = v / 3;
      /* 🌊 The tail beats with how fast it is ACTUALLY going, so a fleeing fish
         looks like one. ⚠️ Floored, or a fish holding station goes rigid. */
      const beat = 0.6 + Math.min(2.5, f.speed / 0.55);
      for (let k = 0; k < card.positions.length; k += 3) {
        /* eslint-disable security/detect-object-injection --
           Loop counters into Float32Arrays this function owns. The same
           reasoning `mesher.ts` and `textures.ts` record for the same rule. */
        const ly = card.positions[k + 1]! * f.length;
        /* ⚠️ The unit card's own z is NOT zero — the second, flat plane of the
           cross lives in it. Dropping it here collapsed the cross back into one
           upright card, which is the fault this whole pair exists to cure. */
        const lz =
          card.positions[k + 2]! * f.length +
          swimOffset(card.along[k / 3]!, seconds, f.phase, f.length, beat);
        /* Turn it to face its heading, then put it where it swims — in the
           terrain's own space, never the world's. */
        const spot = cardOffset(card.positions[k]!, ly, lz, f.length, f.yaw, f.pitch);
        positions[v] = f.x - origin.x + spot.x;
        positions[v + 1] = f.y - origin.y + spot.y;
        positions[v + 2] = f.z - origin.z + spot.z;
        if (this.solid) {
          /* 🔆 The skin's own normal, turned to face the way this fish is
             going, then lit. ⚠️ **Per frame, because the fish turns** — a shade
             baked once would light a fish for a heading it no longer has. */
          const n = rotateNormal(
            card.normals[k]!,
            card.normals[k + 1]!,
            card.normals[k + 2]!,
            f.yaw,
            f.pitch,
          );
          this.shades[v / 3] = shadeFor(n.x, n.y, n.z);
        }
        v += 3;
      }
      uvs.set(card.uvs, base * 2);
      for (let k = 0; k < card.indices.length; k++) indices[i + k] = base + card.indices[k]!;
      /* eslint-enable security/detect-object-injection */
      i += card.indices.length;
    }

    this.stats.near = near.length;
    this.stats.eye = `${eye.x.toFixed(0)},${eye.z.toFixed(0)}`;
    const sample = near[0] ?? null;
    if (sample !== null) {
      this.stats.fish = `${sample.x.toFixed(0)},${sample.z.toFixed(0)}`;
      this.stats.gap = Math.round(Math.hypot(sample.x - eye.x, sample.z - eye.z));
    } else {
      this.stats.fish = 'none';
      this.stats.gap = -1;
    }
    this.stats.drawn = i / this.indicesPerFish;
    this.geometry.setDrawRange(0, i);
    this.geometry.getAttribute('position').needsUpdate = true;
    this.geometry.getAttribute('uv').needsUpdate = true;
    const index = this.geometry.getIndex();
    if (index !== null) index.needsUpdate = true;
    if (this.solid) {
      const shade = this.geometry.getAttribute('shade');
      /* ⚠️ A solid school that never grew has no `shade` yet — `getAttribute`
         returns undefined rather than null for a name it does not hold. */
      if (shade !== undefined) shade.needsUpdate = true;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * 🐟 **The whole sea — one school per species, driven as one thing** — T231.
 *
 * 🎯 **haugur, 2026-09-17:** *"we still have to create 2 more fish for the
 * ocean… Lax and Ufsi"* and *"we have the fish mechanics ready and in place."*
 *
 * ## 🔴 Why three schools rather than one with three pictures
 *
 * **A card is built from its photograph's own width over its height**, and the
 * three cut-outs measure **2.59, 3.09 and 2.82 to one**. Three proportions are
 * three cards, three cards cannot share a vertex buffer, and once they cannot
 * share a buffer **an atlas saves nothing** — it would only add a UV offset to
 * the hot loop that was just fixed for drawing fish upside down.
 *
 * ✅ **And it buys the thing `14_The_Animal_System.md` asks for by name: a fault
 * lands in exactly ONE place.** If the lax is backwards and the ufsi is not,
 * that is one school's arithmetic and not a shared card's.
 *
 * ⚠️ **Three draw calls instead of one.** 📏 Against a chunk mesher on the same
 * thread building 239,492 faces, three draws are nothing — and **the fish count
 * has not gone up at all**, because the server splits one budget of 60 between
 * the three (see `server/src/shoal.ts`).
 */
export class Shoal {
  private readonly schools: readonly FishSchool[];

  /**
   * @param kinds which species to draw. ⚠️ **Defaults to every fish the species
   *   table says is BUILT** — so switching a fish on is one flag in `fish.ts`
   *   and not a list to keep in step here.
   */
  constructor(kinds: readonly string[] = builtSeaFish()) {
    this.schools = kinds.map((kind) => new FishSchool(kind));
  }

  /** Every mesh to add to the scene. */
  get meshes(): readonly Mesh[] {
    return this.schools.map((s) => s.mesh);
  }

  /** Hand the server's word to every school; each keeps only its own kind. */
  setFrom(message: CreaturesMessage, now: number): void {
    for (const school of this.schools) school.setFrom(message, now);
  }

  update(
    seconds: number,
    eye: { x: number; y: number; z: number },
    origin: { x: number; y: number; z: number },
    dt: number,
  ): void {
    for (const school of this.schools) school.update(seconds, eye, origin, dt);
  }

  /**
   * 🔬 **What the whole sea drew, plus a line per species** — T220's instrument,
   * widened.
   *
   * 🔴 **THE PER-SPECIES BREAKDOWN IS THE POINT.** A single total cannot tell
   * *the sea is empty* from *two species are fine and the lax never spawned*,
   * and the lax is the rare one — **6 of 60** — so it is exactly the fish a
   * total would hide. ⚠️ **That is the process failure of the fish session
   * stated as a number:** a probe reported `columns 0` and nobody could tell
   * which fish it meant.
   */
  get stats(): Record<string, unknown> {
    const per: Record<string, unknown> = {};
    let columns = 0;
    let near = 0;
    let drawn = 0;
    let ready = true;
    for (const school of this.schools) {
      per[school.stats.kind] = school.stats;
      columns += school.stats.columns;
      near += school.stats.near;
      drawn += school.stats.drawn;
      if (!school.stats.ready) ready = false;
    }
    return { columns, near, drawn, ready, species: per };
  }

  dispose(): void {
    for (const school of this.schools) school.dispose();
  }
}

/** Every sea fish the species table says is built. */
function builtSeaFish(): readonly string[] {
  return FISH.filter((f) => f.built && f.water !== 'river').map((f) => f.key);
}
